<template>
    <v-card outlined rounded="lg" :class="displaySectionSeparated ? 'mb-6' : ''">
        <div
            :id="`gc-section-wrapper-${sectionId}`"
            class="pa-4"
            @mouseenter="showToolbar = true"
            @mouseleave="showToolbar = false"
        >
            <content-section-toolbar
                v-if="renderToolbar"
                :sectionId="contentSection.sectionId"
                :visible="showToolbar"
                :contentRestrictions="contentSection.contentRestrictions"
                @copy="copyTextClicked"
                @bookmark="bookmarkClicked"
                @print="printClicked"
                @share="shareClicked"
                @fireMobileActions="mobileActionClicked"
            />
            <div class="highlightable" ref="highlightable" tabindex="0">
                <button
                    v-show="fromHighlight.showButton && fromHighlight.action"
                    ref="addNoteButtonFromHighlight"
                    class="add-note-button v-btn primary v-btn--flat v-btn--outlined theme--dark v-size--default"
                    :style="{ top: fromHighlight.buttonTop + 'px', left: fromHighlight.buttonLeft + 'px' }"
                    @click="handleHighlightButtonClick"
                >
                    <v-icon dark v-if="fromHighlight.buttonIcon" class="mr-1">
                        {{ fromHighlight.buttonIcon }}
                    </v-icon>
                    {{ fromHighlight.buttonText }}
                </button>
                <content-section-title
                    :title="contentSection.title"
                    :contentId="contentSection.sectionId"
                    :documentId="contentSection.documentId"
                    :isLink="showTitleAsLink && isLink(contentSection)"
                />

                <content-section-content
                    :content="contentSection.content"
                    :documentId="contentSection.documentId"
                    :sectionId="contentSection.sectionId"
                    :contentModifiers="contentModifiers"
                />
            </div>

            <content-section-insights/>

            <content-section-user-bookmarks
                :defaultSortOrder="userBookmarksDefaultSortOrder"
                @sectionSortOrderChanged="$emit('sectionSortOrderChanged', $event)"
                :expandSection="isNotesSectionExpanded"
                v-if="contentSection?.contentRestrictions?.canBookmark"
                :bookmarks="contentSection.bookmarks"
                :sectionId="contentSection.sectionId"
                :defaultShowAll="showAllBookmarks"
                :showNoteControlMenu="showNoteControlMenu"
            />

            <content-section-breadcrumb
                v-if="displayBreadCrumbs && contentSection?.breadcrumbs?.length !== 0"
                :breadcrumbs="contentSection?.breadcrumbs"
                @breadcrumb-clicked="handleBreadcrumbClick"
            />
        </div>
    </v-card>
</template>

<script>
import ContentSectionToolbar from "./ContentSectionToolbar.vue";
import ContentSectionTitle from "./ContentSectionTitle.vue";
import ContentSectionContent from "./ContentSectionContent.vue";
import ContentSectionInsights from "./ContentSectionInsights.vue";
import ContentSectionUserBookmarks from "./ContentSectionUserBookmarks.vue";
import ContentSectionBreadcrumb from "./ContentSectionBreadcrumb.vue";
import ItemBreadcrumb from "../search/content/result/item_breadcrumb.vue";
import ContentSectionType from "./types/contentSectionType";
import NewToolBarActions from "./mixins/NewToolBarActions";
import {EventNames} from "../../classes/EventNames";

export default {
    components: {
        ItemBreadcrumb,
        ContentSectionBreadcrumb,
        ContentSectionUserBookmarks,
        ContentSectionInsights,
        ContentSectionContent,
        ContentSectionTitle,
        ContentSectionToolbar
    },
    props: {
        /**
         * @type {ContentSectionType}
         */
        contentSection: {
            type: Object,
            required: true
        },
        /**
         * @type {Array<ContentModifier>}
         */
        contentModifiers: {
          type: Array,
          required: true
        },
        isNotesSectionExpanded: {
            type: Boolean,
            required: false,
            default: false
        },
        showAllBookmarks: {
            type: Boolean,
            default: false
        },
        renderToolbar: {
            type: Boolean,
            default: true
        },
        showNoteControlMenu: {
            type: Boolean,
            default: true
        },
        userBookmarksDefaultSortOrder: {
            type: String,
            default: 'desc'
        },
        /**
         * @type {import('vue').PropType<'none' | 'add-note' | 'insert-into'>}
         */
        highlightType: {
            type: String,
            required: false,
            default: 'none',
            validator: (value) => ['none', 'add-note', 'insert-into'].includes(value)
        },
        showTitleAsLink: {
            type: Boolean,
            default: true
        },
        displayBreadCrumbs: {
            type: Boolean,
            default: true
        },
        displaySectionSeparated: {
            type: Boolean,
            default: true
        }
    },
    mixins: [NewToolBarActions],
    data() {
        const highlightDefaults = {
            'add-note': {
                showButton: false,
                buttonTop: 0,
                buttonLeft: 0,
                buttonText: 'Add Note',
                buttonIcon: 'mdi-bookmark-plus-outline',
                action: this.addNoteFromHighlight,
            },
            'insert-into': {
                showButton: false,
                buttonTop: 0,
                buttonLeft: 0,
                buttonText: 'Insert',
                buttonIcon: null,
                action: this.insertNoteIntoCurrentNote,
            },
            'none': {
                showButton: false,
                buttonTop: 0,
                buttonLeft: 0,
                buttonText: '',
                buttonIcon: null,
                action: null,
            }
        };

        return {
            showToolbar: false,
            fromHighlight: highlightDefaults[this.highlightType] || highlightDefaults['none'],
        }
    },
    computed: {
        contentId() {
            return this.contentSection.contentId;
        },
        sectionId() {
            return this.contentSection.sectionId;
        },
        sectionTitle() {
            return this.contentSection.title;
        },
        documentId() {
            return this.contentSection.documentId;
        },
        sectionContent(){
            return this.contentSection.content;
        }
    },
    methods: {
        checkSelection() {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) {
                this.fromHighlight.showButton = false;
                return;
            }

            const range = selection.getRangeAt(0);
            const container = this.$refs.highlightable;

            if (!container.contains(range.commonAncestorContainer)) {
                this.fromHighlight.showButton = false;
                return;
            }

            const rect = range.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            this.fromHighlight.buttonTop = rect.top - containerRect.top - 20;
            this.fromHighlight.buttonLeft = rect.left - containerRect.left + 15 + rect.width / 2;

            this.fromHighlight.showButton = true;
            container.focus();
        },

        handleHighlightButtonClick() {
            if (this.fromHighlight.action) {
                this.fromHighlight.action();
            }
            this.fromHighlight.showButton = false;
            window.getSelection().removeAllRanges();
        },

        addNoteFromHighlight() {
            this.toggleBookmarkActionNew({
                sectionTitle: this.sectionTitle,
                sectionId: this.sectionId,
                documentId: this.documentId,
                quoteText: window.getSelection().toString(),
            });
        },

        insertNoteIntoCurrentNote() {
            EventBus.fire(EventNames.INSERT_QUOTE_INTO_NOTE, {
                'quoteText': window.getSelection().toString(),
            });
        },

        handleBreadcrumbClick(breadcrumbItem) {
            this.$emit('breadcrumb-clicked', breadcrumbItem);
        },

        isLink(section){
            return !section.isChapter;
        },

        copyTextClicked() {
            this.copyTextAction(this.sectionId);
        },

        bookmarkClicked() {
            //RE-ENABLE [PRA-9699]: Restore annotation logic once global component is compatible with content pages.
            this.toggleBookmarkActionNew({
                sectionTitle: this.sectionTitle,
                sectionId: this.sectionId,
                documentId: this.documentId
            });
        },

        printClicked() {
            this.printAction(this.documentId, this.sectionId);
        },

        shareClicked() {
            this.shareLinkAction(this.sectionId);
        },

        mobileActionClicked(){
            const sectionDetails = {
                sectionId: this.sectionId,
                documentId: this.documentId,
                sectionTitle: this.sectionTitle,
                sectionContent: this.sectionContent,
                hasContentPremium: true,
            }
            this.mobileActionFired(sectionDetails);
        },
    },
    mounted() {
        // TODO:: localize?
        document.addEventListener('selectionchange', this.checkSelection);
    },
    beforeDestroy() {
        document.removeEventListener('selectionchange', this.checkSelection);
    }
}
</script>

<style scoped lang="scss">
    //RE-ENABLE [PRA-9699]: Apply highlight css here.
    .apply-border {
        border: 1px solid #757575 !important;
    }

    .add-note-button {
        position: absolute;
        transform: translateX(-50%);
        padding: 0.4rem 0.8rem;
        font-size: 0.9rem;
        cursor: pointer;
        z-index: 10;
    }
</style>